import { Fragment, useCallback, useEffect, useState } from 'react'
import type { ChangeEvent, KeyboardEvent } from 'react';
import { Form, Nav, Tab } from "react-bootstrap";
import MissionService from '../../../../service/MissionService';
import { useParams } from 'react-router';
import { CircularProgress } from '@mui/material';
import OperateService from '../../../../service/OperateService';
import Loader from '../../../../Layouts/Loader/Loader';
import dayjs, { Dayjs } from 'dayjs';
import CustomSelect from '../../../CommonComponents/CustomSelect';
import CustomTimePicker from '../../../CommonComponents/CustomTimePicker';
import DatetimeInput from '../../../CommonComponents/DatetimeInput';
import GroundStationService from '../../../../service/GroundStationService';
import { VALIDATION_RULES } from '../../../../utils/constants';
import { containsSpecialChars, getInvalidRangeErrorMessage, getSpecialCharacterMessage, showToastMessage, validateInputRange } from '../../../../utils/commonFunctions';
import { OrbitObject } from '../utils';
import CustomToolTip from '../../../CommonComponents/CustomToolTip';
import ConfirmationDialog from '../../../CommonComponents/confirmation_dialog/ConfirmationDialog';
import { CloseSvg } from '../../../CommonComponents/svgs/Svgs';
import '../../Satellites/CreateGroupModel.scss';
import '../../Satellites/Task/TaskCreation.css';
import './OperateModal.scss';


interface OperateModalProps {
    close: () => void;
    navigate_to_operate: () => void;
    openModal?: ModalConfig | boolean;
    hasSelectedGroup?: SatelliteGroup | boolean | null;
}

type OrbitType = 'Orbit' | 'TLE' | 'Norad ID';
type OrbitParamKey = 'altitude' | 'eccentricity' | 'inclination' | 'AP' | 'TA' | 'RAAN';

type SatelliteGroup = {
    uniqueId?: string;
    name?: string;
    referenceOrbitParameters?: Partial<Record<OrbitParamKey, number | string>>;
};

type Satellite = {
    uniqueId?: string;
    name?: string;
};

type GroundStationId = string | number;

type GroundStation = {
    groundStationId?: GroundStationId;
    groundStationName?: string;
    properties?: {
        location?: string;
        country?: string;
        latitude?: string | number;
        longitude?: string | number;
        freqbands?: string[];
    };
};

type MissionDetails = {
    gsList?: GroundStationId[];
};

type ModalConfig = {
    type?: string;
};

type OrbitParams = Partial<Record<OrbitParamKey, number | string>> & {
    epoch?: Dayjs | null;
    tle?: string;
    noradId?: string;
};

type SelectOption<T = unknown> = {
    label: string;
    value: T;
};

type OrbitField = {
    label: string;
    name: OrbitParamKey;
    placeholder: string;
    range: [number, number];
    max_length?: number;
    parameter?: string;
};

type ScheduleTelecommand = {
    timestamp: number;
    uniqueId: string;
    linkId?: string;
    operationType: number;
};

type SchedulePayload = {
    scheduleid: string;
    taskPlanName: string;
    taskPlanId: string;
    taskVersion: string;
    startTime: number;
    jobList: {
        startTime: number;
        tcList: ScheduleTelecommand[];
        jobName: string;
        jobId: string;
        GroundStationProvider: number;
    }[];
};

type ApiOrbitResponse = {
    OrbitParameters?: Partial<Record<OrbitParamKey | 'epoch', number | string>>;
};

type ProviderData = Record<string, Record<string, {
    Downlink?: string;
    Uplink?: string;
}>>;

type OrbitValidationRule = {
    MIN: number;
    MAX: number;
    PARAMETER?: string;
};

const ORBIT_TYPE_OPTIONS: OrbitType[] = ['TLE', 'Norad ID', 'Orbit'];
const DISABLED_ORBIT_TYPES: OrbitType[] = ['TLE', 'Norad ID'];
const ORBIT_FIELDS = OrbitObject as OrbitField[];

// Checks whether an orbit type is intentionally unavailable in this modal.
const isDisabledOrbitType = (type: OrbitType) => DISABLED_ORBIT_TYPES.includes(type);

// Reads the modal type while supporting legacy boolean modal props.
const getModalType = (openModal?: ModalConfig | boolean) => typeof openModal === 'object' && openModal !== null ? openModal.type : undefined;

// Narrows the optional selected group prop from older boolean usage.
const isSatelliteGroup = (value: SatelliteGroup | boolean | null | undefined): value is SatelliteGroup => typeof value === 'object' && value !== null;

// Converts latest orbit API data into the local orbit form shape.
const buildOrbitParamsFromApi = (data?: ApiOrbitResponse): OrbitParams => ({
    altitude: data?.['OrbitParameters']?.['altitude'],
    eccentricity: data?.['OrbitParameters']?.['eccentricity'] || 0,
    inclination: data?.['OrbitParameters']?.['inclination'] || 0,
    AP: data?.['OrbitParameters']?.['AP'] || 0,
    TA: data?.['OrbitParameters']?.['TA'] || 0,
    RAAN: data?.['OrbitParameters']?.['RAAN'] || 0,
    epoch: dayjs.utc(Number(data?.['OrbitParameters']?.['epoch']) * 1000)
});

// Builds fallback orbit params from the group's reference orbit.
const buildDefaultOrbitParams = (group: SatelliteGroup): OrbitParams => ({
    altitude: group.referenceOrbitParameters?.altitude,
    eccentricity: group.referenceOrbitParameters?.eccentricity,
    inclination: group.referenceOrbitParameters?.inclination,
    AP: group.referenceOrbitParameters?.AP,
    TA: group.referenceOrbitParameters?.TA,
    RAAN: group.referenceOrbitParameters?.RAAN,
    epoch: dayjs.utc(new Date())
});

// Returns the displayable country name from the station country payload.
const getGroundStationCountry = (country?: string) => country?.includes(',') ? country.split(",")[1]?.trim() : country;

// Converts frequency bands into the same pipe-separated table text used elsewhere.
const getFrequencyBandsText = (freqbands?: string[]) => freqbands?.join(' | ') || '--';

// Returns range validation metadata for a supported orbit field.
const getOrbitValidationRule = (name: OrbitParamKey): OrbitValidationRule => {
    switch (name) {
        case 'altitude':
            return VALIDATION_RULES.ORBITAL_ALTITUDE;
        case 'eccentricity':
            return VALIDATION_RULES.ORBITAL_ECCENTRICITY;
        case 'inclination':
            return VALIDATION_RULES.ORBITAL_INCLINATION;
        case 'RAAN':
            return VALIDATION_RULES.ORBITAL_RAAN;
        case 'AP':
            return VALIDATION_RULES.ORBITAL_AP;
        case 'TA':
            return VALIDATION_RULES.ORBITAL_TA;
    }
};

// Creates connect/disconnect telecommands from provider link data.
const buildTelecommands = (providerData?: ProviderData): ScheduleTelecommand[] => {
    const tcList: ScheduleTelecommand[] = [];

    Object.values(providerData ?? {}).forEach((bands) => {
        Object.values(bands ?? {}).forEach((linkDetails) => {
            const timestamp = Date.now();
            tcList.push(
                {
                    timestamp,
                    uniqueId: "Connect ID",
                    linkId: linkDetails.Downlink,
                    operationType: 2
                },
                {
                    timestamp: timestamp + 10000,
                    uniqueId: "Connect ID",
                    linkId: linkDetails.Uplink,
                    operationType: 2
                }
            );
        });
    });

    return tcList;
};

// Builds the fixed operate schedule payload expected after promotion.
const buildSchedulePayload = (tcList: ScheduleTelecommand[]): SchedulePayload => {
    const startTime = Date.now();

    return {
        scheduleid: "abcd",
        taskPlanName: "taskPlan 1",
        taskPlanId: "task Plan id - 1",
        taskVersion: "ver A",
        startTime,
        jobList: [
            {
                startTime,
                tcList,
                jobName: "task A",
                jobId: "Task Id - A",
                GroundStationProvider: 2 // Use 2 only - fixed
            }
        ]
    };
};

// Validates a single orbit field and shows the matching range message.
const validateOrbitField = (field: OrbitField, value: number | string | null | undefined) => {
    const rule = getOrbitValidationRule(field.name);
    const numericValue = Number(value);

    if (value === '' || value === undefined || value === null || Number.isNaN(numericValue)) {
        showToastMessage('error', `${field.label} is required`)
        return false;
    }

    if (numericValue < rule.MIN || numericValue > rule.MAX) {
        const parameter = 'PARAMETER' in rule ? rule.PARAMETER : '';
        showToastMessage('error', getInvalidRangeErrorMessage(field.label, rule.MIN, rule.MAX, parameter))
        return false;
    }

    return true;
};

// Renders the promote satellite flow without changing existing promote behavior.
const OperateModal: React.FC<OperateModalProps> = ({ close, navigate_to_operate, openModal, hasSelectedGroup }) => {

    const params = useParams();
    const { mission_id } = params
    const [groupsOptions, setGroupsOptions] = useState<SelectOption<SatelliteGroup>[]>([]);
    const [validation, setValidation] = useState(false);
    const [selectedType, setSelectedType] = useState<OrbitType>('Orbit');
    const [selectedGroup, setSelectedGroup] = useState<SelectOption<SatelliteGroup> | null>(null);
    const [selectedSatellite, setSelectedSatellite] = useState<SelectOption<Satellite> | null>(null);
    const [orbitParams, setOrbitParams] = useState<OrbitParams>({})
    const [gsList, setGsList] = useState<GroundStation[]>([])
    const [loading, setLoading] = useState(false)
    const [loadingGroups, setLoadingGroups] = useState(false)
    const [isPromoting, setIsPromoting] = useState(false)
    const [isDateTimeValid, setIsDateTimeValid] = useState(true)
    const [showCancelConfirm, setShowCancelConfirm] = useState(false)
    const [hasChanges, setHasChanges] = useState(false)
    const modalType = getModalType(openModal);

    // Resets orbit params when the selected orbit input type changes.
    const setInitialOrbitParams = useCallback((orbit_type: OrbitType, data: OrbitParams) => {
        if (orbit_type === 'Orbit') {
            setOrbitParams({
                'altitude': data?.['altitude'],
                'eccentricity': data?.['eccentricity'],
                'inclination': data?.['inclination'],
                'AP': data?.['AP'],
                'TA': data?.['TA'],
                'RAAN': data?.['RAAN'],
                'epoch': data?.['epoch']
            })
        } else if (orbit_type === 'TLE' || orbit_type === 'Norad ID') {
            setOrbitParams({
                [orbit_type === 'TLE' ? 'tle' : 'noradId']: ''
            })
        }
    }, [])

    // Loads latest orbit details and falls back to the group's reference orbit.
    const getLatestOrbitDetails = useCallback(async (group: SatelliteGroup) => {
        try {
            const groupId = group?.['uniqueId']
            const res = await MissionService.getLatestOrbitDetails(groupId, 'Launch');
            if (res?.data) {
                setInitialOrbitParams(selectedType, buildOrbitParamsFromApi(res.data))
            }
        } catch {
            const defaultOrbitParams = buildDefaultOrbitParams(group);
            setInitialOrbitParams(selectedType, defaultOrbitParams)
        }
    }, [selectedType, setInitialOrbitParams])

    // Loads the first satellite available for the selected group.
    const getSatellites = useCallback(async (group: SatelliteGroup) => {
        try {
            const groupId = group?.['uniqueId']
            const res = await MissionService.getAllSatellitesByGroupId(mission_id, groupId);
            if (res?.data) {
                const satelliteData = res?.['data']?.[0];
                if (!satelliteData) {
                    setSelectedSatellite(null)
                    return;
                }

                const satellite: SelectOption<Satellite> = {
                    label: satelliteData?.['name'],
                    value: satelliteData
                }
                setSelectedSatellite(satellite)
            }
        } catch (err) {
            console.error(err);
        }
    }, [mission_id])

    // Loads satellite and orbit data together while showing the modal loader.
    const getSatellitesWithOrbitDetails = useCallback(async (groupDetails: SatelliteGroup) => {
        setLoading(true)
        try {
            await getLatestOrbitDetails(groupDetails)
            await getSatellites(groupDetails)
        } catch (error) {
            showToastMessage('error', 'Something went wrong')
        } finally {
            setLoading(false)
        }
    }, [getLatestOrbitDetails, getSatellites])

    // Loads eligible satellite groups or applies the preselected group.
    const getGroups = useCallback(async () => {
        if (isSatelliteGroup(hasSelectedGroup)) {
            const selectedGroupOption = { label: hasSelectedGroup.name ?? '', value: hasSelectedGroup };
            setSelectedGroup(selectedGroupOption)
            setGroupsOptions([selectedGroupOption])
            getSatellitesWithOrbitDetails(hasSelectedGroup);
        } else {
            setLoadingGroups(true)
            try {
                const res = await MissionService.get_eligile_sat_for_promote(mission_id);
                const list = res?.data?.data ?? [];
                const groups: SelectOption<SatelliteGroup>[] = list.map((group: SatelliteGroup) => ({
                    label: group.name ?? '',
                    value: group
                }));

                setGroupsOptions(groups);
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingGroups(false)
            }
        }
    }, [getSatellitesWithOrbitDetails, hasSelectedGroup, mission_id])

    // Loads ground stations attached to the current mission.
    const fetchGroundStations = useCallback(async (missionGsList: GroundStationId[]) => {
        try {
            const res = await GroundStationService.getGroundStationsByMissionId();
            if (res?.data) {
                const gsListForMission = (res.data as GroundStation[]).filter((gs) => {
                    const groundStationId = gs.groundStationId;
                    return groundStationId !== undefined && missionGsList.includes(groundStationId);
                });
                setGsList(gsListForMission)
            }
        } catch (err) {
            console.error(err);
        }
    }, [])

    // Loads mission details so the ground station table has mission-scoped data.
    const getMission = useCallback(async () => {
        try {
            const res = await MissionService.get_mission_by_id(mission_id);
            const missionData = res?.data as MissionDetails | undefined;
            if (missionData?.gsList?.length) {
                fetchGroundStations(missionData.gsList)
            } else {
                setGsList([])
            }
        } catch (err) {
            console.error(err);
        }
    }, [fetchGroundStations, mission_id])

    // Initializes group options whenever the incoming selected group changes.
    useEffect(() => {
        getGroups()
    }, [getGroups])

    // Initializes mission ground stations for the station tab.
    useEffect(() => {
        getMission()
    }, [getMission])



    // Validates currently selected orbit input mode before submit.
    const handleValidate = () => {
        if (selectedType === 'Orbit') {
            const isInvalid = ORBIT_FIELDS.some((item) => !validateOrbitField(item, orbitParams?.[item.name]));
            setValidation(isInvalid)
            return !isInvalid
        } else if (selectedType === 'TLE') {
            if (!orbitParams?.['tle']) {
                setValidation(true)
                showToastMessage('error', "Tle is required")
                return false
            } else {
                setValidation(false)
                return true
            }
        } else if (selectedType === 'Norad ID') {
            if (!orbitParams?.['noradId']) {
                setValidation(true)
                showToastMessage('error', "Norad ID is required")
                return false
            } else {
                setValidation(false)
                return true
            }
        }
        return false
    }

    // Submits promotion payload and schedule data to operate services.
    const handleSubmit = async () => {
        const validation = handleValidate()

        if (validation) {
            setIsPromoting(true)
            const orbt_data = selectedType === 'Orbit' ? {
                orbitParameters: {
                    ...orbitParams,
                    epoch: Math.round((orbitParams?.['epoch']?.valueOf() ?? 0) / 1000)
                }
            } : {
                ...orbitParams
            }

            if (modalType === 'promote') {
                try {
                    const groupId = selectedGroup?.['value']?.['uniqueId'];
                    const satelliteId = selectedSatellite?.['value']?.['uniqueId'];
                    const resPromote = await OperateService.postPromoteSatellite(groupId, satelliteId, mission_id, orbt_data);

                    if (resPromote.data) {
                        const resProvider = await OperateService.getProvider(groupId);
                        const tcList = buildTelecommands(resProvider?.data);
                        const addScheduleData = buildSchedulePayload(tcList);

                        await OperateService.addSchedule(resPromote?.data?.scenarioId, resPromote?.data?.trueTwinId, addScheduleData);
                        setIsPromoting(false);
                        showToastMessage('success', "Satellite promoted successfully");
                        navigate_to_operate()
                        close();
                    }
                } catch (err: unknown) {
                    const responseData = (err as { response?: { data?: string } })?.response?.data;
                    const errorMessage = responseData === 'Satellite is already in operate phase' ? responseData : "Something went wrong while promoting satellite";
                    showToastMessage('error', errorMessage);
                    console.error(err);
                    setIsPromoting(false);
                }
            }
        }
    }

    // Allows only numeric keystrokes in orbit number fields.
    const handleNumericKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.keyCode === 13) { e.preventDefault(); return; }
        if (e.key.length > 1) return;
        if (/[0-9.]/.test(e.key)) return;
        e.preventDefault();
        if (containsSpecialChars(e.key)) {
            showToastMessage('info', getSpecialCharacterMessage());
        } else {
            showToastMessage('info', 'Only numeric values are allowed');
        }
    }

    // Stores epoch in UTC and enforces supported year boundaries.
    const handleScenarioDateTime = (dateTime: Dayjs | null) => {
        setHasChanges(true)
        if (!dateTime || !dateTime.isValid()) {
            setOrbitParams({ ...orbitParams, 'epoch': null })
            return;
        }
        const year = dateTime.year();
        if (year < 2002 || year > 2037) {
            showToastMessage('error', "Year must be between 2002 and 2037");
            setOrbitParams({
                ...orbitParams,
                'epoch': dayjs.utc(new Date())
            })
        } else {
            setOrbitParams({
                ...orbitParams,
                'epoch': dayjs.utc(dateTime)
            })
        }
    }

    // Opens discard confirmation only when the form has local edits.
    const handleClose = () => {
        hasChanges ? setShowCancelConfirm(true) : close()
    }

    // Selects a satellite group and refreshes dependent satellite/orbit data.
    const handleGroupChange = (group: SelectOption<SatelliteGroup> | null) => {
        setHasChanges(true)
        setSelectedGroup(group)
        setSelectedSatellite(null)
        if (group?.value) {
            getSatellitesWithOrbitDetails(group.value)
        }
    }

    // Updates selected orbit type and resets its input model.
    const handleOrbitTypeChange = (type: OrbitType) => {
        setHasChanges(true)
        if (modalType === 'promote') {
            setInitialOrbitParams(type, orbitParams)
        }
        setSelectedType(type)
    }

    // Prevents radio enter key presses from submitting the form.
    const preventEnterSubmit = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.keyCode === 13) {
            e.preventDefault();
        }
    }

    // Updates an orbit number while hard-blocking invalid range input.
    const handleOrbitFieldChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, item: OrbitField) => {
        setHasChanges(true)
        const value = event.target.value;
        const prevVal = String(orbitParams?.[item.name] ?? '');

        if (value === '') {
            setOrbitParams({ ...orbitParams, [item.name]: '' });
            return;
        }

        const validation = validateInputRange(item.label, value, item.range[0], item.range[1], item.max_length, item.parameter)

        if (validation.success) {
            setOrbitParams({
                ...orbitParams,
                [item.name]: validation.value
            })
        } else {
            event.target.value = prevVal;
            showToastMessage('info', validation.error)
        }
    }

    // Validates required selections before triggering the submit flow.
    const handleSubmitClick = () => {
        if (!selectedGroup) {
            showToastMessage('error', 'Please select a Satellite Group')
            return;
        }

        if (selectedType === 'Orbit' && !orbitParams?.['epoch']?.isValid()) {
            showToastMessage('error', 'Please enter a valid Epoch')
            return;
        }

        handleSubmit()
    }

    const isUpdateMode = modalType === 'update';
    const hasExternalOrbitParams = Boolean(orbitParams?.['tle'] || orbitParams?.['noradId']);
    const disableOrbitFields = (isUpdateMode && hasExternalOrbitParams) || !selectedGroup || isPromoting;

    return (
        <Fragment>
            {loading && <Loader />}
            <div className="admin-form operate-promote-modal">
                <div className="createGroupCardSmall admin-form-card operate-promote-modal__card">
                    <div className="createGroupHeader createGroupBorderBottom">
                        <div className='fs-18'>Promote Satellite</div>
                        <div className='cursor-pointer' onClick={handleClose}>
                            <CloseSvg />
                        </div>
                    </div>
                    <div className="createGroupDetailsFormDivGroup createGroupDetailsBoxDiv operate-promote-modal__body">
                        <div className='operate-promote-modal__top-row'>
                            <div className={`createGroupDetailsFormItem ${isUpdateMode ? 'opacity-50 pointer-events-none' : ''}`}>
                                <div className="create-group-model__label">Satellite Groups<span className="opacity-75">*</span></div>
                                <CustomSelect
                                    isDisabled={isUpdateMode || isPromoting}
                                    isLoading={loadingGroups}
                                    isSearchable={false}
                                    controllHeight='32px'
                                    background='rgba(10, 10, 10, 0.65)'
                                    color='#8E8E8E'
                                    padding='0px 8px'
                                    placeholder='Select Satellite Group' options={groupsOptions}
                                    value={selectedGroup}
                                    onChange={handleGroupChange}
                                />
                            </div>
                            <div className='createGroupDetailsFormItem'>
                                <div className="create-group-model__label">
                                    Satellite<span className="opacity-75">*</span>
                                </div>

                                <div className='form-control pointer-events-none operate-promote-modal__readonly d-flex align-items-center justify-content-between'>
                                    <span>
                                        {selectedSatellite?.label ?? (
                                            <span className='opacity-30 fs-12'>Satellite</span>
                                        )}
                                    </span>

                                    {loading && (
                                        <CircularProgress size={16} color='inherit' />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className='operate-promote-modal__tabs'>
                            <Tab.Container id="left-tabs-example" defaultActiveKey="Details">
                                <div className='w-100 d-flex align-items-center justify-content-center'>
                                    <Nav
                                        variant="pills"
                                        className="panel-tabs nav-tabs operate-promote-modal__nav w-auto d-flex justify-content-center"
                                    >
                                        <Nav.Item>
                                            <Nav.Link
                                                eventKey="Details"
                                            >
                                                <span className='fs-16 fw-semibold'>Orbit Details</span>
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link
                                                disabled
                                                eventKey="Station"
                                            >
                                                <span className='fs-16 fw-semibold'>Ground Station</span>
                                            </Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                </div>
                                <Tab.Content>
                                    <Tab.Pane eventKey="Details">
                                        <Form validated={validation} className='operate-promote-modal__form'>
                                            <Form.Group className='createGroupDetailsFormItem fullWidth taskTypeOptionWrapper operate-promote-modal__type'>
                                                <Form.Label className="taskTypeLabel operate-promote-modal__type-label">Select Type</Form.Label>
                                                <div className="taskTypeOptions operate-promote-modal__type-options">
                                                    {ORBIT_TYPE_OPTIONS.map((item, index) => {
                                                        const isDisabled = isDisabledOrbitType(item);

                                                        return (
                                                            <Form.Check
                                                                key={item}
                                                                disabled={isDisabled || isPromoting}
                                                                type="radio"
                                                                label={item}
                                                                name="operateOrbitType"
                                                                id={`operate-${item.replace(/\s+/g, '-').toLowerCase()}`}
                                                                value={item}
                                                                checked={selectedType === item}
                                                                onKeyDown={preventEnterSubmit}
                                                                onChange={() => handleOrbitTypeChange(item)}
                                                                className={`operate-promote-modal__type-check ${index === 0 ? 'operate-promote-modal__type-check--first' : ''}`}
                                                            />
                                                        )
                                                    })}
                                                </div>
                                            </Form.Group>
                                            {selectedType === 'Orbit' &&
                                                <div className='createGroupDetailsFormItem operate-promote-modal__epoch'>
                                                    <div className={`create-group-model__label ${!selectedGroup ? 'opacity-50' : 'opacity-100'}`}>Epoch<span className="opacity-75">*</span></div>
                                                    <DatetimeInput
                                                        disabled={!selectedGroup || isPromoting}
                                                        value={orbitParams?.['epoch'] ? Math.floor(orbitParams?.['epoch']?.valueOf() / 1000) : null}
                                                        onChange={(value) => {
                                                            handleScenarioDateTime(value === null ? null : dayjs.utc(value * 1000));
                                                        }}
                                                        onValidationChange={(isValid) => {
                                                            setIsDateTimeValid(isValid);
                                                        }}
                                                    />
                                                </div>
                                            }
                                            {selectedType === 'Orbit' && <div className='operate-promote-modal__orbit-grid'>
                                                {ORBIT_FIELDS.map((item, index) => (
                                                    <div key={index} className={`createGroupDetailsFormItem`}>
                                                        <div className={`create-group-model__label ${!selectedGroup ? 'opacity-50' : 'opacity-100'}`}>{item.label}<span className="opacity-75">*</span></div>
                                                        <div className={`d-flex gap-2 flex-column w-100 ${disableOrbitFields ? 'opacity-50' : 'opacity-100'}`}>
                                                            <div className="d-flex">
                                                                <Form.Control
                                                                    className={item.parameter ? "inputBoxWithUnitcreateGroup" : "w-100"}
                                                                    min={item?.range?.[0]}
                                                                    max={item?.range?.[1]}
                                                                    disabled={disableOrbitFields}
                                                                    required
                                                                    type="number"
                                                                    step="any"
                                                                    placeholder={item.placeholder}
                                                                    value={orbitParams?.[item.name]}
                                                                    onKeyDown={handleNumericKeyDown}
                                                                    onChange={(event) => handleOrbitFieldChange(event, item)}
                                                                />
                                                                {item.parameter && <span className="unitBoxcreateGroup">{item.parameter}</span>}
                                                            </div>
                                                            <Form.Control.Feedback type="invalid">
                                                                {!orbitParams?.[item.name] ? `${item.label} is required.` : getInvalidRangeErrorMessage(item.label, item?.range?.[0], item?.range?.[1], '')}
                                                            </Form.Control.Feedback>
                                                        </div>
                                                    </div>
                                                ))}

                                            </div>}
                                            {selectedType === 'TLE' &&
                                                <div className="createGroupDetailsFormItem fullWidth">
                                                    <div className="create-group-model__label">TLE<span className="opacity-75">*</span></div>
                                                    <Form.Control
                                                        className="w-100 customDescriptionForGroup"
                                                        as="textarea"
                                                        disabled={isPromoting}
                                                        placeholder="Enter TLE"
                                                        value={orbitParams?.['tle']}
                                                        onChange={(event) => {
                                                            setHasChanges(true)
                                                            setOrbitParams({
                                                                ...orbitParams,
                                                                'tle': event.target.value?.trimStart()
                                                            })
                                                        }}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        Please provide a valid TLE.
                                                    </Form.Control.Feedback>
                                                </div>
                                            }
                                            {selectedType === 'Norad ID' &&
                                                <div className="createGroupDetailsFormItem">
                                                    <div className="create-group-model__label">Norad ID<span className="opacity-75">*</span></div>
                                                    <div className="d-flex flex-column w-100">
                                                        <Form.Control
                                                            className="w-100"
                                                            required
                                                            disabled={isPromoting}
                                                            type="text"
                                                            placeholder="Enter Norad ID"
                                                            value={orbitParams?.['noradId']}
                                                            onChange={(event) => {
                                                                setHasChanges(true)
                                                                setOrbitParams({
                                                                    ...orbitParams,
                                                                    'noradId': event.target.value?.trimStart()
                                                                })
                                                            }}
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            Please provide a valid Norad ID.
                                                        </Form.Control.Feedback>
                                                    </div>
                                                </div>
                                            }
                                        </Form>
                                    </Tab.Pane>

                                    <Tab.Pane eventKey="Station" className='w-100'>
                                        <div className="operate-promote-modal__station-table tableWrapper">
                                            <div className="operate-promote-modal__station-header table-header">
                                                <div className='table-cell operate-promote-modal__station-name-cell'>Ground Stations</div>
                                                <div className='table-cell operate-promote-modal__station-location-cell'>Location</div>
                                                <div className='table-cell operate-promote-modal__station-bands-cell'>Frequency Bands</div>
                                            </div>

                                            <div className='operate-promote-modal__station-body table-body'>
                                                {gsList.length > 0 ? gsList.map((item) => {
                                                    const groundStationId = item.groundStationId;
                                                    const country = getGroundStationCountry(item.properties?.country);
                                                    const frequencyBandsText = getFrequencyBandsText(item.properties?.freqbands);

                                                    return (
                                                        <div key={groundStationId ?? item.groundStationName} className="operate-promote-modal__station-row table-row">
                                                            <div className="table-cell operate-promote-modal__station-name-cell">
                                                                <span className='operate-promote-modal__station-name-text text-truncate' title={item.groundStationName}>
                                                                    {item.groundStationName || '--'}
                                                                </span>
                                                            </div>
                                                            <div className='table-cell operate-promote-modal__station-location-cell'>
                                                                <span className='operate-promote-modal__station-location-line'>
                                                                    <span className='color_history'>Location:</span>
                                                                    <span className='color-location text-truncate' title={`${item.properties?.location ?? '--'}${country ? `, ${country}` : ''}`}>
                                                                        {item.properties?.location || '--'}{country ? `, ${country}` : ''}
                                                                    </span>
                                                                </span>
                                                                <span className='operate-promote-modal__station-coordinates'>
                                                                    <span className='color_history'>Lat: <span className='color-text'>{item.properties?.latitude ?? '--'}</span></span>
                                                                    <span className='color_history'>|</span>
                                                                    <span className='color_history'>Long: <span className='color-text'>{item.properties?.longitude ?? '--'}</span></span>
                                                                </span>
                                                            </div>
                                                            <div className='table-cell operate-promote-modal__station-bands-cell'>
                                                                <CustomToolTip
                                                                    title={frequencyBandsText}
                                                                    placement='top'
                                                                    className='operate-promote-modal__station-bands-tooltip'
                                                                >
                                                                    <span className='operate-promote-modal__station-bands-text'>
                                                                        {frequencyBandsText}
                                                                    </span>
                                                                </CustomToolTip>
                                                            </div>
                                                        </div>
                                                    );
                                                }) : (
                                                    <div className='operate-promote-modal__station-empty'>
                                                        No ground stations found.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </Tab.Pane>
                                </Tab.Content>
                            </Tab.Container>
                        </div>
                    </div>

                    <div className="createGroupBorderBottom w-100 pb-0"></div>
                    <div className="createGroupButtonsDiv">
                        <div className="w-100 d-flex align-items-center justify-content-between">
                            <button className="closeModal__btn_createGroup"
                                onClick={handleClose}>
                                Cancel
                            </button>
                            <button
                                className="createAndConfigureModal_btn_createGroup"
                                disabled={isPromoting || loading || !isDateTimeValid}
                                onClick={handleSubmitClick}
                            >
                                {modalType === 'promote'
                                    ? isPromoting
                                        ? <div className='d-flex gap-2'>
                                            Promoting
                                            <div className="operate-promote-modal__button-spinner">
                                                <CircularProgress size={14} color='inherit' />
                                            </div>
                                        </div>
                                        : 'Promote'
                                    : 'Update'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <ConfirmationDialog
                type="discard"
                open={showCancelConfirm}
                title="Discard Unsaved Changes?"
                description="Are you sure you want to discard your unsaved changes?"
                confirm_button_text="Discard"
                cancel_button_text="Cancel"
                onConfirm={close}
                onCancel={() => setShowCancelConfirm(false)}
            />
        </Fragment>
    )
}

export default OperateModal
